import React from 'react';
import { NavLink, Route, Routes, Navigate } from 'react-router-dom';
import { GeneralSettings } from '@/pages/settings/General';
import { RegionalSettings } from '@/pages/settings/Regional';
import { RolesSettings } from '@/pages/settings/Roles';
import { UsersSettings } from '@/pages/settings/Users';
import { IntegrationsSettings } from '@/pages/settings/Integrations';
import { NotificationsSettings } from '@/pages/settings/Notifications';
import { CommunicationsSettings } from '@/pages/settings/Communications';
import { GeolocationSettings } from '@/pages/settings/Geolocation';
import { BrandingSettings } from '@/pages/settings/Branding';
import { AuditComplianceSettings } from '@/pages/settings/AuditCompliance';
import { DangerZone } from '@/pages/settings/DangerZone';
import { PhasePlaceholder } from '@/pages/settings/PhasePlaceholder';
import { RatingTablesPage } from '@/pages/settings/RatingTables';
import { RiskMatricesPage } from '@/pages/settings/RiskMatrices';
import { MatrixWizardPage } from '@/pages/settings/MatrixWizard';
import { PolicyWordingsPage } from '@/pages/settings/PolicyWordings';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Building2, Globe2, ShieldAlert, Users2, Plug, Bell, Calculator,
  Palette, ScrollText, AlertTriangle, MessageSquareText, FileText, LocateFixed,
} from 'lucide-react';

const nav: Array<{ to: string; icon: React.ReactElement; label: string; chip?: string }> = [
  { to: 'general', icon: <Building2 size={16} />, label: 'General' },
  { to: 'regional', icon: <Globe2 size={16} />, label: 'Regional' },
  { to: 'roles', icon: <ShieldAlert size={16} />, label: 'Roles & Permissions' },
  { to: 'users', icon: <Users2 size={16} />, label: 'Users' },
  { to: 'integrations', icon: <Plug size={16} />, label: 'Integrations' },
  { to: 'geolocation', icon: <LocateFixed size={16} />, label: 'Survey Geolocation' },
  { to: 'communications', icon: <MessageSquareText size={16} />, label: 'Communications' },
  { to: 'notifications', icon: <Bell size={16} />, label: 'Notifications' },
  { to: 'risk-matrices', icon: <Calculator size={16} />, label: 'Risk Matrices' },
  { to: 'policy-wordings', icon: <FileText size={16} />, label: 'Policy Wordings' },
  { to: 'branding', icon: <Palette size={16} />, label: 'Branding & Documents' },
  { to: 'audit', icon: <ScrollText size={16} />, label: 'Audit & Compliance' },
  { to: 'danger', icon: <AlertTriangle size={16} />, label: 'Danger Zone' },
];

const SETTINGS_BASE = '/insurer/settings';

export const SettingsHub: React.FC = () => (
  <div className="space-y-4 animate-fade-in">
    <h2 className="font-headline text-3xl text-raptor-navy tracking-wide">Settings</h2>
    <div className="grid md:grid-cols-[240px_1fr] gap-6">
      <Card className="p-2 h-fit sticky top-24">
        <nav className="space-y-0.5">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={`${SETTINGS_BASE}/${n.to}`}
              data-testid={`settings-nav-${n.to}`}
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 rounded-md text-sm font-body ${isActive ? 'bg-raptor-blue/15 text-raptor-navy font-semibold' : 'text-raptor-navy/70 hover:bg-raptor-neutral/60'}`
              }
            >
              <span className="opacity-80">{n.icon}</span>
              <span className="flex-1">{n.label}</span>
              {n.chip && <Badge variant="muted" className="text-[10px]">{n.chip}</Badge>}
            </NavLink>
          ))}
        </nav>
      </Card>
      <div className="min-w-0">
        <Routes>
          <Route path="" element={<Navigate to="general" replace />} />
          <Route path="general" element={<GeneralSettings />} />
          <Route path="regional" element={<RegionalSettings />} />
          <Route path="roles" element={<RolesSettings />} />
          <Route path="users" element={<UsersSettings />} />
          <Route path="integrations" element={<IntegrationsSettings />} />
          <Route path="geolocation" element={<GeolocationSettings />} />
          <Route path="communications" element={<CommunicationsSettings />} />
          <Route path="notifications" element={<NotificationsSettings />} />
          <Route path="branding" element={<BrandingSettings />} />
          <Route path="audit" element={<AuditComplianceSettings />} />
          <Route path="rating-tables" element={<Navigate to="../risk-matrices" replace />} />
          <Route path="rating-tables/:matrixId" element={<RatingTablesPage />} />
          <Route path="risk-matrices" element={<RiskMatricesPage />} />
          <Route path="risk-matrices/new" element={<MatrixWizardPage />} />
          <Route path="risk-matrices/:matrixId" element={<RatingTablesPage />} />
          <Route path="policy-wordings" element={<PolicyWordingsPage />} />
          <Route path="danger" element={<DangerZone />} />
        </Routes>
      </div>
    </div>
  </div>
);
